import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Inbox, Sparkles, Shield, Zap, ArrowRight } from 'lucide-react';

const HeroSection: React.FC = () => {
  const scrollToPricing = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Email Icons */}
        <div className="absolute top-20 left-[10%] animate-float-slow opacity-20">
          <Mail className="w-16 h-16 text-white" />
        </div>
        <div className="absolute top-40 right-[15%] animate-float-medium opacity-15">
          <Inbox className="w-20 h-20 text-white" />
        </div>
        <div className="absolute bottom-32 left-[20%] animate-float-fast opacity-20">
          <Sparkles className="w-12 h-12 text-white" />
        </div>
        <div className="absolute top-[60%] right-[10%] animate-float-slow opacity-15">
          <Shield className="w-14 h-14 text-white" />
        </div>
        <div className="absolute top-[30%] left-[5%] animate-float-medium opacity-10">
          <Zap className="w-10 h-10 text-white" />
        </div>
        <div className="absolute bottom-20 right-[25%] animate-float-fast opacity-20">
          <Mail className="w-8 h-8 text-white" />
        </div>

        {/* Abstract Gradient Orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-pulse-slow" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse-slow" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500 rounded-full filter blur-3xl opacity-10" />
      </div>

      {/* Navbar */}
      <nav className="relative z-10 px-6 py-4 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-white group transition-transform duration-300 hover:scale-105"
          >
            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm group-hover:bg-white/20 transition-colors duration-300">
              <Mail className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold tracking-tight">InboxOne</span>
          </Link>

          {/* Nav Links */}
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-white/90 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 hover:bg-white/10"
            >
              Login
            </Link>
            <Link
              to="/login"
              className="bg-white text-violet-600 px-5 py-2 rounded-lg font-semibold shadow-lg shadow-violet-900/20 hover:shadow-xl hover:shadow-violet-900/30 transition-all duration-300 hover:scale-105 hover:bg-violet-50"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <main className="relative z-10 flex flex-col items-center justify-center px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-yellow-300" />
            <span className="text-white/90 text-sm font-medium">AI-Powered Email Management</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 leading-tight">
            Take Control of
            <span className="block mt-2 bg-gradient-to-r from-white via-purple-100 to-indigo-100 bg-clip-text text-transparent">
              Your Inbox
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-delay">
            AI-powered email management that automatically detects vendors, organizes your messages, and cleans up the clutter. Spend less time managing email, more time on what matters.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-delay-2">
            <Link
              to="/login"
              className="group flex items-center gap-2 bg-white text-violet-600 px-8 py-4 rounded-xl font-semibold text-lg shadow-2xl shadow-violet-900/30 hover:shadow-violet-900/40 transition-all duration-300 hover:scale-105 hover:bg-violet-50 w-full sm:w-auto justify-center"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a
              href="#pricing"
              onClick={scrollToPricing}
              className="flex items-center gap-2 bg-transparent border-2 border-white/40 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-white/60 transition-all duration-300 hover:scale-105 w-full sm:w-auto justify-center backdrop-blur-sm"
            >
              View Pricing
            </a>
          </div>

        </div>

        {/* Floating Feature Cards */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto w-full animate-fade-in-delay-3">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:bg-white/15 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-2">AI Detection</h3>
            <p className="text-white/70 text-sm">Automatically identifies vendors and categorizes emails</p>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:bg-white/15 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Inbox className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-2">Smart Organization</h3>
            <p className="text-white/70 text-sm">Keep your inbox clean with automatic sorting</p>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:bg-white/15 transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-2">One-Click Cleanup</h3>
            <p className="text-white/70 text-sm">Remove unwanted emails with a single action</p>
          </div>
        </div>
      </main>

      {/* Bottom Wave Decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
            fillOpacity="0.1"
          />
        </svg>
      </div>

      {/* Custom CSS for Animations */}
      <style>{`
        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }

        @keyframes float-medium {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(-5deg);
          }
        }

        @keyframes float-fast {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.05);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-float-medium {
          animation: float-medium 4s ease-in-out infinite;
        }

        .animate-float-fast {
          animation: float-fast 3s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-fade-in-delay {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.2s forwards;
        }

        .animate-fade-in-delay-2 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.4s forwards;
        }

        .animate-fade-in-delay-3 {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out 0.6s forwards;
        }
      `}</style>
    </div>
  );
};

export default HeroSection;
